import React, { useEffect, useRef, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { chatTenantService } from '../../services/chatTenantService';
import type { ChatImageAttachment } from '../../contexts/ChatInDocContext';

interface ChatVisionImagesProps {
  attachments: ChatImageAttachment[];
  workspaceId?: string | null;
  flush?: boolean;
}

interface ChatVisionImageProps {
  item: ChatImageAttachment;
  src?: string;
  fetchFailed: boolean;
  flush: boolean;
  onRetry: () => void;
}

const frameClassName = (flush: boolean): string => {
  if (flush)
  {
    return 'aspect-[4/3] max-h-96 w-full';
  }

  return 'aspect-[4/3] max-h-64 w-full';
};

const ChatVisionImageSkeleton: React.FC<{ flush: boolean }> = ({ flush }) => {
  return (
    <div
      className={`relative overflow-hidden rounded-md ${frameClassName(flush)} ${flush ? 'bg-white/15' : 'bg-gray-200'}`}
      role="status"
      aria-busy="true"
    >
      <div className={`absolute inset-0 animate-pulse ${flush ? 'bg-white/20' : 'bg-gray-300/80'}`} />
      <div className="absolute inset-0 flex items-center justify-center">
        <PhotoIcon
          className={`h-10 w-10 ${flush ? 'text-white/70' : 'text-gray-500'}`}
          aria-hidden="true"
        />
      </div>
      <span className="sr-only">Carregando imagem</span>
    </div>
  );
};

const ChatVisionImageError: React.FC<{ flush: boolean; onRetry: () => void }> = ({ flush, onRetry }) => {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 rounded-md px-3 text-center ${frameClassName(flush)} ${flush ? 'bg-white/10' : 'bg-gray-100'}`}
      role="alert"
    >
      <PhotoIcon
        className={`h-8 w-8 ${flush ? 'text-white/70' : 'text-gray-500'}`}
        aria-hidden="true"
      />
      <p className={`text-sm font-medium ${flush ? 'text-white' : 'text-gray-800'}`}>
        Não foi possível carregar a imagem
      </p>
      <button
        type="button"
        onClick={onRetry}
        className={`inline-flex min-h-[44px] items-center justify-center rounded-md px-3 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
          flush
            ? 'bg-white/20 text-white hover:bg-white/30 focus-visible:outline-white'
            : 'bg-white text-gray-800 ring-1 ring-gray-300 hover:bg-gray-50 focus-visible:outline-blue-600'
        }`}
      >
        Tentar novamente
      </button>
    </div>
  );
};

const ChatVisionImage: React.FC<ChatVisionImageProps> = ({ item, src, fetchFailed, flush, onRetry }) => {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const [imageAttempt, setImageAttempt] = useState(0);
  const loadedSrcRef = useRef(loadedSrc);
  loadedSrcRef.current = loadedSrc;

  const alt = item.fileName?.trim() || 'Imagem da conversa';
  const imageReady = Boolean(src) && loadedSrc === src;
  const imageBroken = fetchFailed || (Boolean(src) && brokenSrc === src);
  const isLocalPreview = Boolean(item.previewUrl);

  const markLoadedIfComplete = (node: HTMLImageElement | null) => {
    if (!node || !src)
    {
      return;
    }

    if (node.complete && node.naturalWidth > 0 && loadedSrcRef.current !== src)
    {
      setLoadedSrc(src);
    }
  };

  const handleRetry = () => {
    setLoadedSrc(null);
    setBrokenSrc(null);
    setImageAttempt((current) => current + 1);
    onRetry();
  };

  if (imageBroken)
  {
    return <ChatVisionImageError flush={flush} onRetry={handleRetry} />;
  }

  if (isLocalPreview && src)
  {
    return (
      <img
        key={imageAttempt}
        src={src}
        alt={alt}
        className={flush
          ? 'block h-auto w-full max-h-96 rounded-md object-cover'
          : 'max-h-64 max-w-full rounded-lg object-contain'}
        onError={() => setBrokenSrc(src)}
      />
    );
  }

  const imageClassName = flush
    ? 'block h-auto w-full max-h-96 rounded-md object-cover'
    : 'max-h-64 max-w-full rounded-lg object-contain';

  return (
    <div className="relative w-full">
      {!imageReady && <ChatVisionImageSkeleton flush={flush} />}
      {src && (
        <img
          key={imageAttempt}
          ref={markLoadedIfComplete}
          src={src}
          alt={imageReady ? alt : ''}
          aria-hidden={imageReady ? undefined : true}
          decoding="async"
          onLoad={() => setLoadedSrc(src)}
          onError={() => setBrokenSrc(src)}
          className={imageReady
            ? `${imageClassName} opacity-100 transition-opacity duration-200`
            : 'pointer-events-none absolute inset-0 h-full w-full rounded-md object-cover opacity-0 transition-opacity duration-200'}
        />
      )}
    </div>
  );
};

export const ChatVisionImages: React.FC<ChatVisionImagesProps> = ({ attachments, workspaceId, flush = false }) => {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [failedPaths, setFailedPaths] = useState<Record<string, true>>({});
  const [reloadToken, setReloadToken] = useState(0);
  const urlsRef = useRef(urls);
  const attachmentsRef = useRef(attachments);
  urlsRef.current = urls;
  attachmentsRef.current = attachments;

  const attachmentKey = attachments
    .map((item) => `${item.filePath}\u0000${item.previewUrl ?? ''}`)
    .join('\n');

  useEffect(() => {
    if (!workspaceId)
    {
      return;
    }

    const pending = attachmentsRef.current.filter((item) => {
      if (item.previewUrl || !item.filePath)
      {
        return false;
      }

      return !urlsRef.current[item.filePath];
    });

    if (pending.length === 0)
    {
      return;
    }

    let cancelled = false;

    void (async () => {
      const nextUrls: Record<string, string> = {};
      const nextFailed: string[] = [];

      await Promise.all(pending.map(async (item) => {
        try
        {
          const response = await api.get<{ url: string }>('/chatindoc/vision-attachments/url', {
            params: {
              workspaceId,
              objectKey: item.filePath,
              ...chatTenantService.buildCustomerIdParams(),
            },
          });

          if (response.data?.url)
          {
            nextUrls[item.filePath] = response.data.url;
            return;
          }

          nextFailed.push(item.filePath);
        }
        catch
        {
          nextFailed.push(item.filePath);
        }
      }));

      if (cancelled)
      {
        return;
      }

      if (Object.keys(nextUrls).length > 0)
      {
        setUrls((current) => ({ ...current, ...nextUrls }));
      }

      if (nextFailed.length > 0 || Object.keys(nextUrls).length > 0)
      {
        setFailedPaths((current) => {
          const next = { ...current };
          for (const path of Object.keys(nextUrls))
          {
            delete next[path];
          }

          for (const path of nextFailed)
          {
            next[path] = true;
          }

          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attachmentKey, reloadToken, workspaceId]);

  const retry = (filePath: string) => {
    setUrls((current) => {
      if (!current[filePath])
      {
        return current;
      }

      const next = { ...current };
      delete next[filePath];
      return next;
    });
    setFailedPaths((current) => {
      if (!current[filePath])
      {
        return current;
      }

      const next = { ...current };
      delete next[filePath];
      return next;
    });
    setReloadToken((current) => current + 1);
  };

  if (attachments.length === 0)
  {
    return null;
  }

  return (
    <div className={flush ? 'flex flex-col' : 'mb-2 flex flex-col gap-2'}>
      {attachments.map((item, index) => {
        const src = item.previewUrl || urls[item.filePath];
        const fetchFailed = !src && (Boolean(failedPaths[item.filePath]) || !item.filePath);

        return (
          <ChatVisionImage
            key={item.filePath || `${item.fileName}-${index}`}
            item={item}
            src={src}
            fetchFailed={fetchFailed}
            flush={flush}
            onRetry={() => retry(item.filePath)}
          />
        );
      })}
    </div>
  );
};
